// COR-186: pool de execução com concorrência limitada, reaproveitável por qualquer tela que
// dispare várias chamadas independentes ao mesmo tempo (hoje: carregarResultadoTodos em
// AuditoriaPage.tsx).
//
// Contexto: a aba "TODOS" já foi migrada de sequencial para Promise.allSettled em 2026-07-09
// (ver comentário em AuditoriaPage.tsx), disparando ~59 requisições simultâneas contra a mesma
// origem. Isso é seguro enquanto a maioria dos módulos serve de uma tabela pré-processada
// (rápido). Mas navegadores limitam a ~6 conexões HTTP/1.1 simultâneas por origem — se QUALQUER
// módulo cair no fallback on-demand (recálculo pesado, sem resultado pré-processado, ex.: Round
// Tripping após a correção da série SGS do BCB voltar a fazer chamadas reais), ele fica na fila
// atrás de dezenas de outras requisições, e o tempo de fila somado ao processamento pode
// facilmente ultrapassar o timeout do cliente HTTP (120s) mesmo que o próprio módulo, isolado,
// responda rápido. Um pool com limite de concorrência (em vez de todas de uma vez) mantém a
// origem sempre com um número de conexões abertas dentro do que o navegador permite, sem voltar
// a ser totalmente sequencial (lento para os módulos rápidos).
export async function executarComLimiteDeConcorrencia<T>(
  tarefas: Array<() => Promise<T>>,
  limite: number
): Promise<PromiseSettledResult<T>[]> {
  const resultados: PromiseSettledResult<T>[] = new Array(tarefas.length);
  let proximoIndice = 0;

  async function worker(): Promise<void> {
    while (proximoIndice < tarefas.length) {
      const indiceAtual = proximoIndice;
      proximoIndice += 1;
      try {
        const valor = await tarefas[indiceAtual]();
        resultados[indiceAtual] = { status: 'fulfilled', value: valor };
      } catch (erro) {
        resultados[indiceAtual] = { status: 'rejected', reason: erro };
      }
    }
  }

  const quantidadeWorkers = Math.max(1, Math.min(limite, tarefas.length));
  await Promise.all(Array.from({ length: quantidadeWorkers }, () => worker()));

  return resultados;
}
